import {
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Box,
  DrawerFooter,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Tooltip,
} from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import { ResolvedLoanFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { colors } from 'packages/common-frontend/colors';
import { IconButton } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { truncateUuid } from 'packages/common-frontend/utils';
import { useCreateInvoiceContext } from 'packages/invoices/hooks/useCreateInvoice';
import { LoanDetailsSideDrawerBody } from 'packages/invoices/ui/create/LoanDetailsSideDrawer';

const CreateInvoiceAddComponentSideDrawerHeader = ({
  onCloseDrawer,
  selectedLoanName,
}: {
  onCloseDrawer: () => void;
  selectedLoanName: string | null;
}) => {
  return (
    <>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        m="24px 24px 0 24px"
        p="1rem"
      >
        <Typography variant="section-title" trim>
          {selectedLoanName ? `${selectedLoanName}` : 'Add Loan to Invoice'}
        </Typography>
        <Button variant="v2-ghost" onClick={onCloseDrawer}>
          <Icon icon="x" />
        </Button>
      </Flex>
      <Divider />
    </>
  );
};

const CreateInvoiceAddComponentSideDrawerFooter = ({
  selectedLoanId,
  setSelectedLoanId,
}: {
  selectedLoanId: string | null;
  setSelectedLoanId: (newLoanId: string | null) => void;
}) => {
  const navigate = useNavigate();

  if (!selectedLoanId) {
    return null;
  }

  return (
    <Flex w="100%" justifyContent="space-between">
      <Button
        onClick={() => setSelectedLoanId(null)}
        leftIcon={<Icon icon="chevron-left" />}
        variant="v2-ghost"
      >
        <Typography fontWeight="bold">Back</Typography>
      </Button>
      <Button
        onClick={() => navigate(`/loans/${selectedLoanId}`)}
        rightIcon={<Icon icon="chevron-right" />}
        variant="v2-ghost"
      >
        <Typography fontWeight="bold">View Loan</Typography>
      </Button>
    </Flex>
  );
};

const LoanSelectorTableRow = ({
  loan,
  setSelectedLoanId,
  onCloseDrawer,
}: {
  loan: ResolvedLoanFragmentGQLType;
  setSelectedLoanId: (newLoanId: string) => void;
  onCloseDrawer: () => void;
}) => {
  const {
    addInvoiceComponent,
    invoice,
    updateInvoice,
    selectedEntities: {
      loan: { updateLoan },
    },
  } = useCreateInvoiceContext();

  const { dueDate: invoiceDueDate } = invoice;

  const onClickAddLoan = useCallback(() => {
    const accrualStartDate =
      loan.invoices?.mostRecentAccrualEndDate ?? loan.dates.originationDate;

    // use due date for accrual end date if feasible
    const accrualEndDate =
      invoice.dueDate && invoice.dueDate > accrualStartDate
        ? invoice.dueDate
        : commonDate.addDays(accrualStartDate, 1);

    addInvoiceComponent({
      invoiceComponentType: 'loan',
      finleyLoanId: loan.id,
      accrualStartDate,
      accrualEndDate,
      totalAmount: 0,
    });
    setSelectedLoanId(loan.id);
    onCloseDrawer();

    // @dani multi-invoice todo: remove these two once we deprecate finleyLoanId on the parent invoice
    updateInvoice({ field: 'finleyLoanId', value: loan.id });
    updateInvoice({ field: 'accrualStartDate', value: accrualStartDate }); // @dani multi-invoice todo: deprecate. currently used to keep parent invoice entity in sync with component
    updateInvoice({ field: 'accrualEndDate', value: accrualEndDate }); // @dani multi-invoice todo: deprecate. currently used to keep parent invoice entity in sync with component
    updateLoan(loan);
  }, [
    addInvoiceComponent,
    loan,
    setSelectedLoanId,
    onCloseDrawer,
    updateInvoice,
    updateLoan,
    invoice,
  ]);

  const isDueDateBeforeLoanOriginationDate = useMemo(() => {
    return !!invoiceDueDate && invoiceDueDate < loan.dates.originationDate;
  }, [invoiceDueDate, loan.dates.originationDate]);

  return (
    <Tr>
      <Td>{loan.name}</Td>
      <Td>
        {loan.externalLoanId
          ? `${truncateUuid(loan.id)} (${loan.externalLoanId})`
          : truncateUuid(loan.id)}
      </Td>
      <Td>
        {commonDate.fullDateStrToHumanDateString(loan.dates.maturityDate)}
      </Td>
      <Td>
        <Flex alignItems="center" gap="8px">
          <Tooltip
            label={
              isDueDateBeforeLoanOriginationDate && invoiceDueDate
                ? `Cannot add loan that originated on ${commonDate.fullDateStrToHumanDateString(
                    loan.dates.originationDate,
                  )} which is after the invoice due date of ${commonDate.fullDateStrToHumanDateString(
                    invoiceDueDate,
                  )}.`
                : undefined
            }
          >
            <Button
              onClick={onClickAddLoan}
              variant="secondary"
              colorScheme="v2-aurora"
              isDisabled={isDueDateBeforeLoanOriginationDate}
            >
              Add
            </Button>
          </Tooltip>
          <IconButton
            ariaLabel="View Loan"
            label="View Loan"
            icon={<Icon icon="chevron-right" />}
            onClick={() => setSelectedLoanId(loan.id)}
          />
        </Flex>
      </Td>
    </Tr>
  );
};

const LoanSelectorHeaderCell = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <Th fontSize="1rem" py="1rem">
      {children}
    </Th>
  );
};

const LoansSelectorTable = ({
  setSelectedLoanId,
  onCloseDrawer,
}: {
  setSelectedLoanId: (newLoanId: string) => void;
  onCloseDrawer: () => void;
}) => {
  const {
    invoiceComponentsWithInvoiceItems,
    selectedEntities: {
      creditFacility: { creditFacilityLoans },
    },
  } = useCreateInvoiceContext();

  const availableCreditFacilityLoans = useMemo(() => {
    const doesLoanQualifyForPaydown = (loan: ResolvedLoanFragmentGQLType) =>
      (loan.funding.totalOutstandingBalance &&
        commonUtils.jsRound({
          value: loan.funding.totalOutstandingBalance,
          precision: 2,
        }) > 0) ||
      commonUtils.jsRound({
        value: loan.fees.accruedInterest.totalAmount,
        precision: 2,
      }) > 0 ||
      commonUtils.jsRound({
        value: loan.fees.accruedServiceFees.totalAmount,
        precision: 2,
      }) > 0;

    return creditFacilityLoans?.filter(
      (loan) =>
        doesLoanQualifyForPaydown(loan) &&
        !invoiceComponentsWithInvoiceItems.some(
          (invoiceComponentWithItems) =>
            invoiceComponentWithItems.invoiceComponent.finleyLoanId === loan.id,
        ),
    );
  }, [creditFacilityLoans, invoiceComponentsWithInvoiceItems]);

  return (
    <Table cursor="default" border="1px" borderColor={colors.gray[200]}>
      <Thead>
        <Tr>
          <LoanSelectorHeaderCell>Name</LoanSelectorHeaderCell>
          <LoanSelectorHeaderCell>Loan ID</LoanSelectorHeaderCell>
          <LoanSelectorHeaderCell>Maturity Date</LoanSelectorHeaderCell>
          <LoanSelectorHeaderCell>Actions</LoanSelectorHeaderCell>
        </Tr>
      </Thead>
      <Tbody>
        {!availableCreditFacilityLoans?.length ? (
          <Tr>
            <Td colSpan={4}>No available loans to add.</Td>
          </Tr>
        ) : (
          availableCreditFacilityLoans?.map((loan) => (
            <LoanSelectorTableRow
              key={loan.id}
              loan={loan}
              setSelectedLoanId={setSelectedLoanId}
              onCloseDrawer={onCloseDrawer}
            />
          ))
        )}
      </Tbody>
    </Table>
  );
};

const CreateInvoiceAddComponentSideDrawerBody = ({
  selectedLoan,
  setSelectedLoanId,
  onCloseDrawer,
}: {
  selectedLoan: ResolvedLoanFragmentGQLType | null;
  setSelectedLoanId: (newLoanId: string) => void;
  onCloseDrawer: () => void;
}) => {
  return (
    <Box>
      {selectedLoan ? (
        <LoanDetailsSideDrawerBody loan={selectedLoan} />
      ) : (
        <LoansSelectorTable
          setSelectedLoanId={setSelectedLoanId}
          onCloseDrawer={onCloseDrawer}
        />
      )}
    </Box>
  );
};

type CreateInvoiceAddComponentSideDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};
export const CreateInvoiceAddComponentSideDrawer = ({
  isOpen,
  onClose,
}: CreateInvoiceAddComponentSideDrawerProps) => {
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);

  const {
    selectedEntities: {
      creditFacility: { creditFacilityLoans },
    },
  } = useCreateInvoiceContext();

  const selectedLoan: ResolvedLoanFragmentGQLType | null = useMemo(() => {
    return (
      creditFacilityLoans?.find((loan) => loan.id === selectedLoanId) ?? null
    );
  }, [selectedLoanId, creditFacilityLoans]);

  const onCloseSideDrawer = () => {
    onClose();
    setSelectedLoanId(null);
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="xl"
      // workaround for known chakra issue where drawer body is not scrollable
      // https://github.com/chakra-ui/chakra-ui/issues/1862
      blockScrollOnMount={false}
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader padding="0">
          <CreateInvoiceAddComponentSideDrawerHeader
            onCloseDrawer={onCloseSideDrawer}
            selectedLoanName={selectedLoan?.name ?? null}
          />
        </DrawerHeader>

        <DrawerBody p="40px">
          <CreateInvoiceAddComponentSideDrawerBody
            selectedLoan={selectedLoan}
            setSelectedLoanId={setSelectedLoanId}
            onCloseDrawer={onCloseSideDrawer}
          />
        </DrawerBody>
        <DrawerFooter>
          <CreateInvoiceAddComponentSideDrawerFooter
            selectedLoanId={selectedLoanId}
            setSelectedLoanId={setSelectedLoanId}
          />
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
